import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile as useProfileOptimized } from "@/hooks/useProfileOptimized";
import { Shield, AlertTriangle, Lock, Mail, RefreshCw } from "lucide-react";

// Import sub-components
import { AdminLoadingState } from "./admin-2fa/AdminLoadingState";
import { AdminForbiddenState } from "./admin-2fa/AdminForbiddenState";
import { AdminLockoutState } from "./admin-2fa/AdminLockoutState";
import { AdminVerificationForm } from "./admin-2fa/AdminVerificationForm";

// Security settings
const TWO_FA_TTL_HOURS = 4;
const OTP_RESEND_COOLDOWN_MS = 60000;
const MAX_OTP_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000;

// Storage keys
const getStorageKeys = (userId: string) => ({
  session: `admin2fa:${userId}`,
  attempts: `admin2fa_attempts:${userId}`,
  lockout: `admin2fa_lockout:${userId}`,
  lastSend: `admin2fa_lastsend:${userId}`,
});

// Session validation result
type SessionCheckResult = {
  isLockedOut: boolean;
  lockoutRemaining: number;
  attempts: number;
  resendCooldown: number;
  sessionId: string | null;
};

// Check session storage for lockout/attempts/cooldown and any stored server session id.
// NOTE: Validity of the session id MUST be confirmed server-side via `has_valid_admin_2fa_session`.
const checkExistingSession = (userId: string): SessionCheckResult => {
  const keys = getStorageKeys(userId);

  try {
    // Check lockout first
    const lockoutData = sessionStorage.getItem(keys.lockout);
    if (lockoutData) {
      const { until } = JSON.parse(lockoutData);
      const remaining = until - Date.now();
      if (remaining > 0) {
        return {
          isLockedOut: true,
          lockoutRemaining: Math.ceil(remaining / 60000),
          attempts: 0,
          resendCooldown: 0,
          sessionId: null,
        };
      } else {
        sessionStorage.removeItem(keys.lockout);
        sessionStorage.removeItem(keys.attempts);
      }
    }

    // Check attempt count
    const attemptsData = sessionStorage.getItem(keys.attempts);
    const attempts = attemptsData ? parseInt(attemptsData, 10) || 0 : 0;

    // Look up any stored server-issued session id
    let sessionId: string | null = null;
    const sessionData = sessionStorage.getItem(keys.session);
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed && typeof parsed.session_id === "string") {
          sessionId = parsed.session_id;
        }
      } catch {
        sessionStorage.removeItem(keys.session);
      }
    }

    // Check resend cooldown
    const lastSend = sessionStorage.getItem(keys.lastSend);
    let resendCooldown = 0;
    if (lastSend) {
      const elapsed = Date.now() - parseInt(lastSend, 10);
      if (elapsed < OTP_RESEND_COOLDOWN_MS) {
        resendCooldown = Math.ceil((OTP_RESEND_COOLDOWN_MS - elapsed) / 1000);
      }
    }

    return {
      isLockedOut: false,
      lockoutRemaining: 0,
      attempts,
      resendCooldown,
      sessionId,
    };
  } catch (e) {
    console.error("Error checking 2FA session:", e);
    return {
      isLockedOut: false,
      lockoutRemaining: 0,
      attempts: 0,
      resendCooldown: 0,
      sessionId: null,
    };
  }
};

interface Props {
  children: React.ReactNode;
}

type AuthState = 
  | "loading"
  | "unauthorized"
  | "forbidden"
  | "locked_out"
  | "needs_verification"
  | "sending_otp"
  | "awaiting_code"
  | "verifying"
  | "verified";

export const AdminRoute: React.FC<Props> = ({ children }) => {
  const { user, loading: authLoading, session } = useAuth();
  const { profile, loading: profileLoading } = useProfileOptimized();
  const location = useLocation();

  const [authState, setAuthState] = useState<AuthState>("loading");
  const [code, setCode] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [hasSuperAdmin, setHasSuperAdmin] = useState<boolean | null>(null);
  // Authorization is determined solely by the server-verified `user_roles` entry.
  // The legacy `profiles.role` column is never trusted here.
  const isAllowed = hasSuperAdmin === true;

  // Check for super_admin role
  useEffect(() => {
    let active = true;
    if (!user) { 
      setHasSuperAdmin(null); 
      return; 
    }
    
    (async () => {
      try {
        const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "super_admin" as any });
        if (active) setHasSuperAdmin(Boolean(data));
      } catch (e) {
        if (active) setHasSuperAdmin(false);
      }
    })();
    
    return () => { active = false; };
  }, [user?.id]);

  // Main state machine - determines auth state based on all factors
  useEffect(() => {
    // Still loading auth or profile
    if (authLoading || profileLoading) {
      setAuthState("loading");
      return;
    }

    // No user - redirect to home
    if (!user) {
      setAuthState("unauthorized");
      return;
    }

    // Still checking super_admin status
    if (hasSuperAdmin === null) {
      setAuthState("loading");
      return;
    }

    // User doesn't have permission
    if (!isAllowed) {
      setAuthState("forbidden");
      return;
    }

    // Check existing lockout/cooldown + any stored server session id
    const sessionCheck = checkExistingSession(user.id);

    if (sessionCheck.isLockedOut) {
      setLockoutRemaining(sessionCheck.lockoutRemaining);
      setAuthState("locked_out");
      return;
    }

    setAttempts(sessionCheck.attempts);
    setResendCooldown(sessionCheck.resendCooldown);

    // If a session id is present, validate it server-side. Forged sessionStorage
    // entries will fail this check because no matching DB row exists.
    if (sessionCheck.sessionId) {
      setAuthState("loading");
      let cancelled = false;
      (async () => {
        try {
          const { data, error } = await supabase.rpc("has_valid_admin_2fa_session", {
            p_session_id: sessionCheck.sessionId!,
          });
          if (cancelled) return;
          if (!error && data === true) {
            setAuthState("verified");
          } else {
            sessionStorage.removeItem(getStorageKeys(user.id).session);
            setAuthState("needs_verification");
          }
        } catch {
          if (!cancelled) {
            sessionStorage.removeItem(getStorageKeys(user.id).session);
            setAuthState("needs_verification");
          }
        }
      })();
      return;
    }

    setAuthState("needs_verification");
  }, [authLoading, profileLoading, user?.id, hasSuperAdmin, isAllowed]);

  // Auto-send OTP when entering needs_verification state
  useEffect(() => {
    if (authState === "needs_verification" && user && resendCooldown === 0) {
      sendOtp();
    }
  }, [authState]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    
    const timer = setInterval(() => {
      setResendCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Lockout timer
  useEffect(() => {
    if (authState !== "locked_out" || !user) return;
    
    const timer = setInterval(() => {
      const keys = getStorageKeys(user.id);
      const lockoutData = sessionStorage.getItem(keys.lockout);
      
      if (lockoutData) {
        const { until } = JSON.parse(lockoutData);
        const remaining = until - Date.now();
        
        if (remaining <= 0) {
          sessionStorage.removeItem(keys.lockout);
          sessionStorage.removeItem(keys.attempts);
          setAttempts(0);
          setLockoutRemaining(0);
          setAuthState("needs_verification");
        } else {
          setLockoutRemaining(Math.ceil(remaining / 60000));
        }
      }
    }, 30000);
    
    return () => clearInterval(timer);
  }, [authState, user?.id]);

  const sendOtp = useCallback(async () => {
    if (!session || !user) return;
    
    const keys = getStorageKeys(user.id);
    
    // Check cooldown
    const lastSend = sessionStorage.getItem(keys.lastSend);
    if (lastSend) {
      const elapsed = Date.now() - parseInt(lastSend, 10);
      if (elapsed < OTP_RESEND_COOLDOWN_MS) {
        setResendCooldown(Math.ceil((OTP_RESEND_COOLDOWN_MS - elapsed) / 1000));
        setAuthState("awaiting_code");
        return;
      }
    }
    
    setAuthState("sending_otp");
    setError(null);
    
    try {
      const { error } = await supabase.functions.invoke("send-admin-otp", { body: {} });
      if (error) throw error;
      
      sessionStorage.setItem(keys.lastSend, Date.now().toString());
      setResendCooldown(Math.ceil(OTP_RESEND_COOLDOWN_MS / 1000));
      setAuthState("awaiting_code");
    } catch (err: any) {
      console.error("send-admin-otp error", err);
      setError(err?.message || "Failed to send verification code");
      setAuthState("awaiting_code");
    }
  }, [session, user?.id]);

  const verifyOtp = useCallback(async () => {
    if (!code.trim() || !user) {
      setError("Enter the 6-digit code");
      return;
    }
    
    if (code.length !== 6) {
      setError("Code must be exactly 6 digits");
      return;
    }
    
    setAuthState("verifying");
    setError(null);
    
    const keys = getStorageKeys(user.id);
    
    try {
      const { data, error } = await supabase.rpc("verify_admin_otp", { p_code: code.trim() });
      
      if (error) throw error;
      
      // Server returns jsonb: { success, session_id?, reason?, attempts_remaining? }
      const result = (data ?? {}) as { success?: boolean; session_id?: string; reason?: string; attempts_remaining?: number; locked_until?: string };

      if (result.success === true && result.session_id) {
        // Clear attempts on success
        sessionStorage.removeItem(keys.attempts);
        setAttempts(0);

        // Store the server-issued session id. This id is meaningless without
        // a matching DB row, so forged sessionStorage entries cannot bypass 2FA.
        sessionStorage.setItem(keys.session, JSON.stringify({
          session_id: result.session_id,
        }));

        // Audit log
        await supabase.rpc("log_admin_action", {
          p_action: "admin_dashboard_2fa_verified",
          p_metadata: { path: location.pathname },
        });

        setAuthState("verified");
      } else {
        // Track failed attempt (mirror server-side counter)
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        sessionStorage.setItem(keys.attempts, newAttempts.toString());
        
        const serverLocked = result.reason === "locked_out";
        const remaining = typeof result.attempts_remaining === "number"
          ? result.attempts_remaining
          : Math.max(0, MAX_OTP_ATTEMPTS - newAttempts);
        
        if (serverLocked || remaining <= 0 || newAttempts >= MAX_OTP_ATTEMPTS) {
          // Prefer server-supplied locked_until (cumulative cross-session lockout);
          // fall back to local duration only if the server didn't supply one.
          const serverUntilMs = result.locked_until ? Date.parse(result.locked_until) : NaN;
          const lockoutUntil = Number.isFinite(serverUntilMs)
            ? serverUntilMs
            : Date.now() + LOCKOUT_DURATION_MS;
          sessionStorage.setItem(keys.lockout, JSON.stringify({ until: lockoutUntil }));
          setLockoutRemaining(Math.max(1, Math.ceil((lockoutUntil - Date.now()) / 60000)));
          
          await supabase.rpc("log_admin_action", {
            p_action: "admin_2fa_lockout",
            p_metadata: { attempts: newAttempts, path: location.pathname, server_reason: result.reason },
          });
          
          setAuthState("locked_out");
        } else {
          setError(`Invalid or expired code. ${remaining} attempts remaining.`);
          setAuthState("awaiting_code");
        }
        setCode("");
      }
    } catch (err: any) {
      console.error("verify_admin_otp error", err);
      setError(err?.message || "Verification failed");
      setAuthState("awaiting_code");
    }
  }, [code, user?.id, attempts, location.pathname]);

  // Handle Enter key for verification
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && authState === "awaiting_code" && code.length === 6) {
      verifyOtp();
    }
  }, [verifyOtp, authState, code]);

  // Render based on state
  switch (authState) {
    case "loading":
      return <AdminLoadingState />;
    
    case "unauthorized":
      return <Navigate to="/" replace />;
    
    case "forbidden":
      return <AdminForbiddenState />;
    
    case "locked_out":
      return <AdminLockoutState lockoutRemaining={lockoutRemaining} />;
    
    case "needs_verification":
    case "sending_otp":
    case "awaiting_code":
    case "verifying":
      return (
        <AdminVerificationForm
          authState={authState}
          code={code}
          setCode={setCode}
          error={error}
          attempts={attempts}
          maxAttempts={MAX_OTP_ATTEMPTS}
          resendCooldown={resendCooldown}
          ttlHours={TWO_FA_TTL_HOURS}
          onVerify={verifyOtp}
          onResend={sendOtp}
          onKeyDown={handleKeyDown}
        />
      );
    
    case "verified":
      return <>{children}</>;
    
    default:
      return <AdminLoadingState />;
  }
};

export default AdminRoute;
